import { useState } from "react";
import { parseEmsDate } from "../../Logic/emsDates";
import { MailboxDirectionMode, MailboxHealthCard, MailboxRunOutcome, MailboxSideOutcome } from "../MailboxAPI";
import { givenUp, isFault, mailboxStanding, MailboxStanding } from "../mailboxHealth";
import Measured from "./Measured";

interface iMailboxRowProps {
    card: MailboxHealthCard;
}

const STANDING_LABEL: Record<MailboxStanding, string> = {
    connectFailed: "Unreachable",
    runFailed: "Failed",
    stalled: "Stalled",
    waiting: "Waiting",
    neverRun: "Never run",
    healthy: "OK",
    disabled: "Off",
};

/** What each per-side outcome's title tells the reader. */
const OUTCOME_TITLE: Record<MailboxRunOutcome, string> = {
    Success: "The run completed",
    Failed: "The run reached the mailbox and failed while processing",
    ConnectFailed: "The run could not reach the mailbox at all",
    LockTimeout: "Another collector node held the lock, so this run measured nothing",
};

/**
 * A `Record`, not an `if`-chain, and deliberately so.
 *
 * An `if`-chain here would fall through to the OK class for anything it did
 * not recognise — so a fifth `MailboxRunOutcome` would compile clean (it is
 * still forced into `OUTCOME_TITLE` above by that table's `Record`), and
 * render green: a failure reported as healthy. Same hazard `mailboxHealth.ts`
 * documents for `STANDING`, and this project's most-repeated defect — a
 * signal quietly becoming its own opposite. A `Record` instead means a fifth
 * outcome fails to compile until it is classified here too.
 *
 * `LockTimeout` reads as "measured nothing" rather than as a failure —
 * another collector node held the lock (contract §2.5). Rendering it as a
 * fault would paint a page red for lock contention, which is not a problem.
 */
const SIDE_CLASS: Record<MailboxRunOutcome, string> = {
    Success: "mp-mb-side mp-mb-ok",
    Failed: "mp-mb-side mp-mb-bad",
    ConnectFailed: "mp-mb-side mp-mb-bad",
    // Quiet, not alarm: this run measured nothing, which is not a fault.
    LockTimeout: "mp-mb-side mp-mb-off",
};

/**
 * What each run outcome is called on screen.
 *
 * Raw enum names are for the wire and for `title` attributes, not for
 * operators — phase 1's TenantTable humanises every enum it shows, and
 * "ConnectFailed" sat one column away from the Standing badge calling the same
 * fact "Unreachable". `LockTimeout` reads as "Not measured" because that is
 * what it means: another collector node held the lock, so this run measured
 * nothing. The full explanation stays in OUTCOME_TITLE.
 */
const SIDE_LABEL: Record<MailboxRunOutcome, string> = {
    Success: "OK",
    Failed: "Failed",
    ConnectFailed: "Unreachable",
    LockTimeout: "Not measured",
};

/** `Legacy` and `Cloud` both mean enabled; they name a transport, not a health. */
function enablementLabel(side: "In" | "Out", mode: MailboxDirectionMode): string {
    return mode === "Disabled" ? `${side} off` : `${side} ${mode.toLowerCase()}`;
}

/**
 * The label the row is known by, and the accessible name of its expander.
 *
 * Trims and treats a blank `name` as absent, the same rule
 * `mailboxHealth.ts`'s `tieBreakLabel` applies for sort order — a card with
 * `name: ""` must not render this row's button with an empty accessible name
 * while the comparator sorts it as unlabelled. Not shared with
 * `tieBreakLabel` itself: that function is private to the pure module, whose
 * purpose is sort-key derivation rather than display, and duplicating three
 * lines is better than coupling a component to a comparator's internals. The
 * two must keep agreeing, though — if this fallback changes, check that one.
 */
function displayName(card: MailboxHealthCard): string {
    const name = card.name?.trim();
    if (name) {
        return name;
    }
    const address = card.address?.trim();
    return address ? address : `Mailbox ${card.mailboxID}`;
}

/**
 * Formats an offset-less, database-sourced run timestamp.
 *
 * `parseEmsDate` is mandatory here. `lastRun.startedAt` carries no offset
 * (contract §1.3), so a bare `new Date()` reads it in the browser's timezone
 * and shifts it by the browser's offset from Africa/Johannesburg. `asOf` is
 * the opposite case and is not this component's concern.
 */
function runClock(value: string | undefined): string | undefined {
    const parsed = parseEmsDate(value);
    return parsed?.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function SideBadge({ side, outcome }: { side: "receive" | "send"; outcome: MailboxSideOutcome }) {
    return (
        <span
            className={SIDE_CLASS[outcome.outcome]}
            title={`${side}: ${OUTCOME_TITLE[outcome.outcome]}`}
        >
            {SIDE_LABEL[outcome.outcome]}
        </span>
    );
}

/**
 * The dot beside the mailbox name.
 *
 * Reuses phase 1's five dot classes rather than inventing a second colour
 * vocabulary for the same meanings. `mp-dot-never` in particular is the colour
 * phase 1 gives `NeverCollected`, which is what `neverRun` is one level down.
 *
 * `waiting` and `healthy` share the OK dot deliberately. Backlog is its own
 * column now, so the number carries what a distinct dot used to — `waiting`
 * survives as a SORT input, ranking a backlogged mailbox above an idle one
 * without claiming anything is wrong with it.
 *
 * A `Record`, so an eighth standing cannot compile until it has a dot (there
 * are seven today). An `if`-chain would fall through to whatever it returned
 * last, which is how a new standing ends up rendering as healthy.
 */
const STANDING_DOT: Record<MailboxStanding, string> = {
    connectFailed: "mp-dot mp-dot-fail",
    runFailed: "mp-dot mp-dot-fail",
    stalled: "mp-dot mp-dot-stale",
    neverRun: "mp-dot mp-dot-never",
    waiting: "mp-dot mp-dot-ok",
    healthy: "mp-dot mp-dot-ok",
    disabled: "mp-dot mp-dot-off",
};

/**
 * One required count.
 *
 * A zero is REAL DATA — the query ran and found nothing — so it renders as
 * `0`, never as `—`. It is styled quiet, which is what keeps a roster of
 * switched-off mailboxes calm while leaving any non-zero number to stand out
 * against it. That contrast is the reason the counts became columns.
 *
 * Deliberately not `Measured`: that component's job is telling absent from
 * zero, and these counts are required, so routing them through it would claim
 * a distinction the wire does not carry. It supplies the same two classes,
 * which is all that is borrowed here.
 */
function Count({ value }: { value: number }) {
    return <span className={value === 0 ? "mp-zero" : "mp-num"}>{value.toLocaleString()}</span>;
}

/**
 * The `Last run` cell: `completedAt`, and NEVER a fallback to `startedAt`.
 *
 * A fallback would make one column mean two different things depending on the
 * data — the exact mistake phase 1 documents for `lastAttemptAt`, where a
 * shared formatter states the second meaning while displaying the first. So
 * when there is no completion the cell says what IS true: `running` if a run
 * is in progress, `—` otherwise. The start time is in the expander, labelled.
 */
function LastRun({ card }: { card: MailboxHealthCard }) {
    const completed = runClock(card.lastRun?.completedAt);
    if (completed) {
        return <>{completed}</>;
    }
    if (card.processingNow) {
        return (
            <span
                className="mp-mb-running"
                title="A collection run is in progress right now"
            >
                running
            </span>
        );
    }
    return (
        <span
            className="mp-absent"
            title="This mailbox has no completed run"
        >
            —
        </span>
    );
}

function MailboxRow({ card }: iMailboxRowProps) {
    const [expanded, setExpanded] = useState(false);

    const standing = mailboxStanding(card);
    const name = displayName(card);
    const stopped = givenUp(card);
    // `lastRun` is absent on most real cards (see MailboxAPI.ts) — never read
    // through unconditionally, here or below.
    const started = runClock(card.lastRun?.startedAt);
    const completed = runClock(card.lastRun?.completedAt);

    /*
        A switched-off direction dims its whole group — a background tint, NOT a
        changed glyph. The zeros inside stay zeros because they are real: the
        query ran and found nothing. The tint says the pipeline they describe is
        not running. Inventing a third meaning for `—` was the alternative and
        is refused: one glyph, one meaning, across this whole feature.

        This is also the payoff for splitting by direction at all — a mailbox
        that receives but does not send is visible without the expander.
    */
    const inDim = card.enablement.inbound === "Disabled" ? " mp-mb-dim" : "";
    const outDim = card.enablement.outbound === "Disabled" ? " mp-mb-dim" : "";

    return (
        <>
            <tr className={isFault(standing) ? "mp-mb-row mp-mb-row-fault" : "mp-mb-row"}>
                <td>
                    <button
                        type="button"
                        className="mp-mb-toggle"
                        aria-expanded={expanded}
                        onClick={() => setExpanded(current => !current)}
                    >
                        <span
                            className="mp-mb-caret"
                            aria-hidden="true"
                        >
                            {expanded ? "⌄" : "›"}
                        </span>
                        {/*
                            The dot replaces the Standing and Enabled columns.
                            For a switched-off mailbox those said "Off" and
                            "In off / Out off" — the same fact twice, on nine
                            rows out of ten. `title` keeps the word available
                            on hover without spending a column on it.

                            `role="img"` and `aria-label` carry the same word to
                            a screen reader, which `title` on its own does not:
                            an empty span has no role and no text for an
                            accessible name to attach to. The label rather than
                            visually-hidden text, so no text node lands inside
                            the mailbox-name cell.
                        */}
                        <span
                            role="img"
                            aria-label={STANDING_LABEL[standing]}
                            className={STANDING_DOT[standing]}
                            title={STANDING_LABEL[standing]}
                        />
                        <span className="mp-mb-name">{name}</span>
                    </button>
                    {/* Only when it says something the name did not already. */}
                    {card.address && card.address !== name && <div className="mp-mb-addr">{card.address}</div>}
                </td>

                {/*
                    The ONE absent-vs-zero cell on this endpoint. A measured 0
                    is "nothing waiting"; absent is "this mailbox did not
                    report", which is what makes the tenant's Core backlog
                    total a partial sum. `Measured`'s default title is written
                    for a per-tenant cell, so the per-mailbox cause needs its own.

                    Under Inbound only, and correctly: backlog is mail waiting
                    to be collected, so it has no outbound meaning.
                */}
                <td className={"mp-r mp-boundary" + inDim}>
                    <Measured
                        value={card.lastBacklog}
                        absentTitle="This mailbox did not report a backlog, so it is missing from the tenant's total"
                    />
                </td>
                <td className={"mp-r" + inDim}>
                    <Count value={card.counts.inbound.stalled} />
                </td>
                <td className={"mp-r" + inDim}>
                    <Count value={card.counts.inbound.failed} />
                </td>
                <td className={"mp-r" + inDim}>
                    <Count value={stopped.inbound} />
                </td>
                {/*
                    The window is disclosed HERE per row, because the column
                    label cannot name it when mailboxes disagree — it degrades
                    to a bare `Flow`. Dropping this title is what made
                    flowHeader's comment false in an earlier draft.
                */}
                <td
                    className={"mp-r" + inDim}
                    title={`Received over the last ${card.throughput.windowHours} hours`}
                >
                    <Count value={card.throughput.received} />
                </td>

                <td className={"mp-r mp-boundary" + outDim}>
                    <Count value={card.counts.outbound.stalled} />
                </td>
                <td className={"mp-r" + outDim}>
                    <Count value={card.counts.outbound.failed} />
                </td>
                <td className={"mp-r" + outDim}>
                    <Count value={stopped.outbound} />
                </td>
                <td
                    className={"mp-r" + outDim}
                    title={`Sent over the last ${card.throughput.windowHours} hours`}
                >
                    <Count value={card.throughput.sent} />
                </td>

                <td className="mp-boundary">
                    <LastRun card={card} />
                </td>
            </tr>

            {expanded && (
                <tr className="mp-mb-detail">
                    <td colSpan={11}>
                        <div className="mp-mb-counts">
                            <div className="mp-mb-cgroup">
                                <div className="mp-mb-clabel">Last run</div>
                                <div className="mp-mb-cvals">
                                    {/*
                                        Render the absence, not two badges guessed at
                                        from nothing. A never-run mailbox has no
                                        receive/send outcome to show — `?.`-ing into
                                        SideBadge would force a fake outcome or render
                                        two blanks, either of which claims knowledge
                                        the server never sent.
                                    */}
                                    {card.lastRun ? (
                                        <>
                                            <SideBadge
                                                side="receive"
                                                outcome={card.lastRun.receive}
                                            />
                                            <SideBadge
                                                side="send"
                                                outcome={card.lastRun.send}
                                            />
                                        </>
                                    ) : (
                                        <span
                                            className="mp-absent"
                                            title="This mailbox has no recorded run"
                                        >
                                            —
                                        </span>
                                    )}
                                    {/*
                                        Both times, labelled, which is what lets the
                                        Last run COLUMN show only `completedAt` without
                                        losing the start (§5.5). `—` means "not
                                        measured" everywhere in this feature and must
                                        not acquire a second meaning, so each dash
                                        carries its own reason.
                                    */}
                                    <span>
                                        {started ?? (
                                            <span
                                                className="mp-absent"
                                                title="No start time was recorded for this run"
                                            >
                                                —
                                            </span>
                                        )}{" "}
                                        →{" "}
                                        {completed ?? (
                                            <span
                                                className="mp-absent"
                                                title="This run has no recorded completion — it may still be in progress"
                                            >
                                                —
                                            </span>
                                        )}
                                    </span>
                                </div>
                            </div>
                            {/*
                                NOT optional, and phase 1 is why. `derive.ts`'s
                                `rowGivenUp` merges dead-lettered with abandoned in
                                the overview on a stated condition: "that distinction
                                is needed on the tenant mailbox view where the
                                operator acts, not in this overview."

                                This is that view. Contract §3.4: Retry on an
                                Abandoned email re-arms the send counter and returns
                                it to Sendable, while on a DeadLettered one it queues
                                a fresh build and returns Queued. Different actions.
                                An operator reading a merged `Given up: 320` cannot
                                tell which the mail needs.

                                So the COLUMN merges for scanning and this SPLITS for
                                acting. Removing it would make derive.ts's comment
                                false without editing it — an earlier draft of this
                                plan did exactly that.
                            */}
                            <div className="mp-mb-cgroup">
                                <div className="mp-mb-clabel">Given up</div>
                                <div className="mp-mb-cvals">
                                    <span>in dead-lettered {card.counts.inbound.deadLettered.toLocaleString()}</span>
                                    <span>out dead-lettered {card.counts.outbound.deadLettered.toLocaleString()}</span>
                                    <span>out abandoned {card.counts.outbound.abandoned.toLocaleString()}</span>
                                </div>
                            </div>
                            {/*
                                `Legacy` and `Cloud` both mean enabled — they name a
                                transport, not a health, which is why this is here
                                rather than in a column. The dim on a direction's
                                group already says whether it is off.
                            */}
                            <div className="mp-mb-cgroup">
                                <div className="mp-mb-clabel">Transport</div>
                                <div className="mp-mb-cvals">
                                    <span>{enablementLabel("In", card.enablement.inbound)}</span>
                                    <span>{enablementLabel("Out", card.enablement.outbound)}</span>
                                </div>
                            </div>
                        </div>
                        {card.lastRun?.receive.error && (
                            <div className="mp-mb-err">receive: {card.lastRun.receive.error}</div>
                        )}
                        {card.lastRun?.send.error && <div className="mp-mb-err">send: {card.lastRun.send.error}</div>}
                        {card.lastConnectionError && (
                            <div className="mp-mb-err">connection: {card.lastConnectionError}</div>
                        )}
                    </td>
                </tr>
            )}
        </>
    );
}

export default MailboxRow;
